import styled from "styled-components";
import { PopupModal } from "../../../components/TopBar/PopupModal/PopupModal";
import { useMemo, useState } from "react";
import { AddToShipment } from "../../Allocation/AddToShipment";
import {
  useSplitPOLinesMutation,
  useSplitTfOrdLinesMutation,
  useUpdateDispatchPurchaseAllocationsMutation,
  useUpdateDispatchTfAllocationsMutation,
} from "../../../../../store/apiSlice/orderApiSlice";
import { useNotification } from "../../../../../hooks/useNotification";

/**
 * ListMenuOptions Component
 *
 * Displays a dropdown menu with actions that can be performed on selected purchase or transfer allocations,
 * such as adding to shipment, updating shipment, and splitting lines.
 *
 * @component
 * @param {Function} closeModalMenuModal - Function to close the dropdown menu.
 * @param {"purchase" | "transfer"} allocation - Type of allocation, used to determine behavior.
 * @param {Array} selectedOrderIds - Array of selected allocation IDs for bulk actions.
 * @param {Function} setSelectedOrder - Function to reset selected orders after an action.
 * @param {Array} selectedOrderFromListview - Full data objects of selected orders, used for validation.
 */

export const ListMenuOptions = ({
  closeModalMenuModal,
  allocation,
  selectedOrderIds,
  setSelectedOrder,
  selectedOrderFromListview,
}) => {
  const [isOpenAddToShipmentModal, setIsOpenAddToShipmentModal] = useState(false);
  const [updateDispatchQtyForPurchaseAllocation] = useUpdateDispatchPurchaseAllocationsMutation();
  const [updateDispatchQtyForTransferAllocation] = useUpdateDispatchTfAllocationsMutation();
  const [splitPOLines] = useSplitPOLinesMutation();
  const [splitTfOLines] = useSplitTfOrdLinesMutation();

  const notify = useNotification();
  const openAddToShipmentModal = () => {
    setIsOpenAddToShipmentModal(true);
  };
  const closeAddToShipmentModal = () => {
    setIsOpenAddToShipmentModal(false);
    closeModalMenuModal();
  };

  const handleUpdateShipment = async () => {
    let res = null;
    try {
      if (allocation === "purchase") {
        res = await updateDispatchQtyForPurchaseAllocation({
          ids: selectedOrderIds,
        }).unwrap();
      } else {
        res = await updateDispatchQtyForTransferAllocation({
          ids: selectedOrderIds,
        }).unwrap();
      }

      notify({
        type: "success",
        message: res.detail,
      });
    } catch (error) {
      let message = "Something went wrong";
      if (error.data?.detail) {
        message = error.data.detail;
      }
      notify({ type: "error", message });
    }
    setSelectedOrder([]);
    closeModalMenuModal();
  };

  const split = async () => {
    try {
      const splitLines = allocation === "purchase" ? splitPOLines : splitTfOLines;
      await splitLines({ ids: selectedOrderIds }).unwrap();
      notify({ type: "success", message: `Successfully splited the ${allocation} order line(s).` });
      setSelectedOrder([]);
      closeModalMenuModal();
    } catch (error) {
      notify({
        type: "error",
        message: error.data?.detail || `Failed to split the ${allocation} order line(s).`,
      });
    }
  };

  const isDisabled = useMemo(() => {
    return (
      selectedOrderFromListview.length < 1 ||
      selectedOrderFromListview.some(item => {
        const orderedQty = Number(item.ordered_qty);
        const allocatedQty = Number(item.allocated_qty);
        const dispatchedQty = Number(item.dispatched_qty);
        return (
          orderedQty < allocatedQty ||
          orderedQty < dispatchedQty ||
          allocatedQty < dispatchedQty ||
          dispatchedQty <= 0
        );
      })
    );
  }, [selectedOrderFromListview]);

  const isDisabledUpdateShipment =
    selectedOrderFromListview.some(
      item =>
        Number(item.allocated_qty) <= Number(item.dispatched_qty) || !Number(item.dispatched_qty),
    ) || !selectedOrderIds.length;

  return (
    <PopupModal styleOfModal={styleOfPopup} closeModal={closeModalMenuModal}>
      {isOpenAddToShipmentModal && (
        <AddToShipment
          closeModal={closeAddToShipmentModal}
          allocation={allocation}
          ordersName={selectedOrderIds}
          setSelectedOrder={setSelectedOrder}
        />
      )}

      <ListMenuOptionsCss>
        <ul>
          {allocation !== "purchase" && (
            <li
              className={`optionItem ${selectedOrderIds.length > 0 ? "" : "disableItem"} `}
              onClick={openAddToShipmentModal}
            >
              Add to Shipment{" "}
            </li>
          )}
          <li
            className={`optionItem ${isDisabledUpdateShipment ? "disableItem" : null} `}
            onClick={handleUpdateShipment}
          >
            Update Shipment{" "}
          </li>
          <li className={`optionItem ${isDisabled ? "disableItem" : ""} `} onClick={split}>
            Split{" "}
          </li>
        </ul>
      </ListMenuOptionsCss>
    </PopupModal>
  );
};

export const ListMenuOptionsCss = styled.div`
  display: flex;
  padding: 4px 20px;
  flex-direction: column;
  align-items: flex-start;
  width: max-content;
  min-width: 180px;
  box-sizing: border-box;
  white-space: nowrap;
  border-radius: 4px;
  border: 1px solid var(--PEARL, #ecf1f4);
  background-color: var(--Conditional-pop-over, #fff);

  /* drop-shadow/0.12+0.8+0.5 */
  box-shadow:
    0px 3px 6px -4px rgba(0, 0, 0, 0.12),
    0px 6px 16px 0px rgba(0, 0, 0, 0.08),
    0px 9px 28px 8px rgba(0, 0, 0, 0.05);

  ul {
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;
    white-space: nowrap;
  }

  ul > li {
    list-style: none;
    white-space: nowrap;
  }
  ul > .optionItem {
    padding: 5px 12px;
    color: var(--DARK-GREY, #0e0e2c);
    font-weight: 400;
    line-height: 22px;
    white-space: nowrap;
    box-sizing: border-box;
    > a {
      color: var(--DARK-GREY, #0e0e2c);
      white-space: nowrap;
    }

    &:hover {
      color: #1249bf;
      cursor: pointer;
    }
  }
  .optionItem.selectedItem {
    background-color: var(--Silver, #dadff3);
  }
  ul > .disableItem {
    cursor: text;
    pointer-events: none;
    color: rgba(0, 0, 0, 0.25);
    a {
      color: rgba(0, 0, 0, 0.25);
      pointer-events: visible;
      white-space: nowrap;
    }
  }
`;

export const styleOfPopup = {
  position: "absolute",
  top: "6.5vh",
  right: 24,
  width: "max-content",
};
